import React from 'react';
import { Box, Chip, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { flatten, has, isEmpty, isEqual, isNil, map } from 'lodash';
import { Link } from 'react-router-dom';

import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh-no-border.svg';
import { GreySpinner } from 'new/components/icons/GreySpinner/GreySpinner';
import RoutePrefixContext from 'new/contexts/RoutePrefixContext';
import { SemanticSensitivityBadge } from 'new/elements/SemanticSensitivityBadge/SemanticSensitivityBadge';
import { SemanticSchemaGenerateButton } from 'new/elements/SemanticSchemaGenerateButton/SemanticSchemaGenerateButton';
import { TooltipWithTitle } from 'new/elements/TooltipWithTitle';
import { isOperational } from 'new/utils/nexsetUtils';
import nexsetTypes from 'new/constants/nexsetTypes';

import CopyText from 'js/components/elements/CopyText';
import Tooltip from 'js/components/elements/mui/Tooltip';
import Button from 'js/components/elements/editors/mui/Button';
import DocEditor from 'js/components/elements/editors/DocEditor';
import TagsEditor from 'js/components/elements/tags/TagsEditor';
import TagsView from 'js/components/elements/tags/TagsView';
import ApiServerParameterTable from 'js/components/data_sources/cards/configViews/ApiServerParameterTable';
import { MOCK_SYSLOG_DETECTED_SCHEMA } from 'js/constants/mockDataSchemas';
import { EXT_CATALOG_ENABLED } from '../../../../constants/config';
import { EXTERNAL_INTEGRATIONS } from '../../../../constants/externalIntegrations';
import {
  ROUTE_API_KEYS,
  ROUTE_DATA_SOURCES,
  ROUTE_DESTINATIONS,
  ROUTE_MY_DATA_SETS,
} from '../../../../constants/pathnames';
import { RESOURCE_TYPE_DATA_SET } from '../../../../constants/resourceTypes';
import { SearchFilterTypes } from '../../../../constants/visibilityFilters';
import { FLOW_TYPE_API_SERVER } from 'js/constants/pipelineConstants';

import DateUtils from '../../../../lib/DateUtils';
import UIUtils from '../../../../lib/UIUtils';
import { isSharedDataset } from 'js/lib/URLUtils';
import {
  getDatasetDataUrl,
  getDatasetFeedUrlSampleRequest,
  getDatasetSyncPayloadString,
  getExtCatalogReference,
  getExtCatalogSettings,
  getUsableApiKey,
  isDatasetSyncable,
  sanitizeApiKeyURL,
} from '../../../../lib/URLUtils';
import { getHttpMethodColor, hasEndpointConfiguration } from 'js/lib/ApiServerUtils';

import { renderFeedApiView } from './utils/renderFeedApiView';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import styles from '../DataSetCardOverviewTab.module.scss';
import { OverviewTab as SharedDatasetOverviewTab } from 'new/components/marketplace/components/DataProductsDrawer/components/Tabs/OverviewTab';

const BUTTON_STYLE = {
  height: 'auto',
};

class DataSetCardOverviewTab extends React.Component {
  static contextType = RoutePrefixContext;

  componentDidMount() {
    this.props.loadDataSet(this.props.data.id);
    this.props.fetchDatasetDoc(this.props.data.id);
  }

  _generateNexsetDataLink = () => {
    this.props.addDatasetApiKey(this.props.data.id);
  };

  _handleCatalogSubmitClicked = () => {
    this.props.submitToCatalog(this.props.data.id);
  };

  _handleCatalogRefreshClicked = () => {
    this.props.fetchDataSet(this.props.data.id);
  };

  _renderDetectedDatasetContent = () => {
    const { source } = this.props;

    const sourceInfoDiv = source ? (
      <Link
        to={`${this.context}${ROUTE_DATA_SOURCES}/${source.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {source.name ? source.name : source.id}
      </Link>
    ) : (
      '-'
    );

    return (
      <div className="detail-col">
        <div className="detail-key">Detected From Source</div>
        <div className="detail-value">{sourceInfoDiv}</div>
      </div>
    );
  };

  _renderDerivedDatasetContent = () => {
    const { parentDataset, isDerivedFromExternalDataset } = this.props;

    //parent info:
    let parentDivContent = '';
    if (parentDataset) {
      parentDivContent = (
        <Link
          to={`${this.context}${ROUTE_MY_DATA_SETS}/${parentDataset.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {parentDataset.name}
        </Link>
      );
    }

    return (
      <div className="detail-col">
        <div className="detail-key">Created From Nexset</div>
        <div className="detail-value">{parentDivContent}</div>
      </div>
    );
  };

  _renderSidebarContent = () => {
    const { data, isDetected, summaryData, dataSinks, readOnly, datasetSemanticInfo } = this.props;

    const createdDate = DateUtils.convertDateStringToFormat(data.created_at);

    const parentInfo = isDetected
      ? this._renderDetectedDatasetContent()
      : this._renderDerivedDatasetContent();

    const derivedDataSetsCount =
      summaryData && has(summaryData, 'data_sets.total') ? summaryData.data_sets.total : null;

    const sharersCount = UIUtils.getDatasetSharersCount(data, summaryData);

    const tagsView =
      readOnly === true ? (
        <TagsView data={data} />
      ) : (
        <TagsEditor id={data.id} resourceType={RESOURCE_TYPE_DATA_SET} />
      );

    const isSyncable = isDatasetSyncable(data);
    const isOperationalNexset = isOperational(data);
    const datasetSyncInfo = this._renderFeedApiView(isSyncable);
    const extCatalogInfo = this._renderCatalogView();

    return (
      <section style={{ padding: '0 16px' }}>
        <div className="detail-row">
          <div className="detail-col">
            <div className="detail-key">Nexset ID</div>
            <div className="detail-value">{data.id}</div>
          </div>
        </div>
        <div className="detail-row">
          <div className="detail-col">
            <div className="detail-key">Created On</div>
            <div className="detail-value">{createdDate}</div>
          </div>
        </div>
        {(!isOperationalNexset || data.nexset_type === 'splitter') && (
          <>
            <div className="detail-row">{parentInfo}</div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key"># Of Transformed Nexsets</div>
                <div className="detail-value">
                  <Link
                    to={`${this.context}${ROUTE_MY_DATA_SETS}?search=${SearchFilterTypes.PARENT_DATA_SET_ID}:${data.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {derivedDataSetsCount}
                  </Link>
                </div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Shared with Users</div>
                <div className="detail-value">{sharersCount}</div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Writing to Destination</div>
                <div className="detail-value">
                  {isEmpty(dataSinks)
                    ? '(None)'
                    : flatten(
                        map(dataSinks, (dataSink) => [
                          <Link
                            key={dataSink.id}
                            to={`${this.context}${ROUTE_DESTINATIONS}/${dataSink.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {dataSink.name}
                          </Link>,
                          ', ',
                        ]),
                      ).slice(0, -1)}
                </div>
              </div>
            </div>
            {datasetSemanticInfo?.dataset_sensitivity && (
              <div className="detail-row">
                <div className="detail-col" style={{ width: 'auto' }}>
                  <div className="detail-key">Sensitivity</div>
                  <div className="detail-value">
                    <SemanticSensitivityBadge semanticInfo={datasetSemanticInfo} size="lg" />
                  </div>
                </div>
              </div>
            )}
            {EXT_CATALOG_ENABLED && extCatalogInfo}
            {isSyncable && datasetSyncInfo}
            {!(data.nexset_type === nexsetTypes.splitter) && (
              <div className="detail-row">
                <div className="detail-col">
                  <div className="detail-key">Tags</div>
                  <div className="detail-value" style={{ marginTop: 'var(--spacing-sm)' }}>
                    {tagsView}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    );
  };

  _renderCatalogView = () => {
    const { data } = this.props;
    const extCatalog = getExtCatalogSettings(data);
    let extCatalogRef = null;
    let extCatalogInfo = null;
    if (extCatalog.enabled === true && extCatalog.catalog && has(extCatalog.catalog, 'id')) {
      extCatalogRef = getExtCatalogReference(data, extCatalog.catalog.id);
      let catalogName;
      let extCatalogView = null;
      //get catalog name from catalog config
      if (has(extCatalog, 'catalog.name') && !isNil(extCatalog.catalog.name)) {
        catalogName = extCatalog.catalog.name;
        //check if this name matches a key in EXTERNAL_INTEGRATIONS. If so, use the meta.display_name from corresponding object

        if (has(EXTERNAL_INTEGRATIONS, catalogName)) {
          catalogName = EXTERNAL_INTEGRATIONS[catalogName].meta.displayName;
        }
      } else {
        catalogName = 'Catalog';
      }

      if (isNil(extCatalogRef) || extCatalogRef.status === 'NONE') {
        //no reference. Show registration button.
        extCatalogView = (
          <div className="detail-value">
            <Button color="primary" variant="ghost" onClick={this._handleCatalogSubmitClicked}>
              <span>{`Register on ${catalogName}`}</span>
            </Button>
          </div>
        );
      } else {
        switch (extCatalogRef.status) {
          case 'ERROR': {
            //catalog integration failed
            extCatalogView = (
              <div className="detail-value">
                {
                  <Button
                    color="primary"
                    variant="ghost"
                    onClick={this._handleCatalogSubmitClicked}
                  >
                    <span>{`Register on ${catalogName}`}</span>
                  </Button>
                }
                <p
                  style={{
                    fontSize: 11,
                    color: 'var(--color-secondary-500)',
                    whiteSpace: 'normal',
                    wordBreak: 'keep-all',
                  }}
                >
                  We couldn't register with {catalogName}. Please try again.
                </p>
              </div>
            );

            break;
          }

          case 'PENDING': {
            // show pending status. disable button
            extCatalogView = (
              <div
                className="detail-value"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 'var(--spacing-md)',
                }}
              >
                <Button
                  color="info"
                  variant="ghost"
                  disabled={true}
                  onClick={this._handleCatalogSubmitClicked}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-lg)',
                    }}
                  >
                    <CircularProgress size={16} />
                    <span>Registering</span>
                  </div>
                </Button>
                <TooltipWithTitle
                  placement="bottom"
                  arrow={true}
                  title={<div>Retry registration</div>}
                  description={<div>Force re-registration with the catalog</div>}
                >
                  <Button
                    color="info"
                    variant="outlined"
                    sx={{
                      border: '1px solid var(--color-greyscale-200)',
                      borderRadius: '16px',
                      minWidth: '32px',
                      minHeight: '32px',
                      padding: 'var(--spacing-xs)',
                      width: '32px',
                      height: '32px',
                      fill: 'var(--color-greyscale-600)',
                      '&:hover': {
                        backgroundColor: 'var(--color-greyscale-50)',
                      },
                    }}
                    onClick={this._handleCatalogRefreshClicked}
                  >
                    {this.props.isLoading.catalogUpdate === true ? (
                      <GreySpinner />
                    ) : (
                      <RefreshIcon width={16} height={16} />
                    )}
                  </Button>
                </TooltipWithTitle>
              </div>
            );

            break;
          }
          case 'DONE': {
            //show catalog info
            const catalogLink = `/${extCatalogRef.info.reference_id}`;
            extCatalogView = has(extCatalogRef.info, 'link') ? (
              <div
                className="detail-value"
                style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-md)' }}
              >
                <a
                  href={extCatalogRef.info.link}
                  className={styles.catalogRefLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${extCatalogRef.info.reference_id}`}
                </a>
                <TooltipWithTitle
                  placement="bottom"
                  arrow={true}
                  title={<div>Update Catalog Entry</div>}
                  description={
                    <div
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'keep-all',
                        maxWidth: '250px',
                      }}
                    >
                      Update the catalog entry with latest information.
                    </div>
                  }
                >
                  <Button
                    color="info"
                    variant="outlined"
                    sx={{
                      border: '1px solid var(--color-greyscale-200)',
                      borderRadius: '16px',
                      minWidth: '32px',
                      minHeight: '32px',
                      padding: 'var(--spacing-xs)',
                      width: '32px',
                      height: '32px',
                      fill: 'var(--color-greyscale-600)',
                      '&:hover': {
                        backgroundColor: 'var(--color-greyscale-50)',
                      },
                    }}
                    onClick={this._handleCatalogSubmitClicked}
                  >
                    {this.props.isLoading.catalogUpdate === true ? (
                      <GreySpinner />
                    ) : (
                      <RefreshIcon width={16} height={16} />
                    )}
                  </Button>
                </TooltipWithTitle>
              </div>
            ) : (
              <div className="detail-value">
                {`Reference ID: ${extCatalogRef.info.reference_id}`}
              </div>
            );

            break;
          }
          default: {
            extCatalogView = (
              <div className="detail-value">
                <Skeleton animation="wave" variant="rounded" width="150px" height="20px" />
              </div>
            );
          }
        }
      }

      extCatalogInfo = (
        <div className="detail-row single-col">
          <div className="detail-col">
            <div className="detail-key">{`${catalogName} Reference`}</div>
            {extCatalogView}
          </div>
        </div>
      );
    }
    return extCatalogInfo;
  };

  _renderFeedApiView = (isSyncable = false) => {
    const { data, apiKeys, isLoading } = this.props;
    return renderFeedApiView({
      config: {
        isSyncable,
        data,
        apiKeys,
        isLoading,
        generateNexsetDataLink: this._generateNexsetDataLink,
        routePrefix: this.context,
      },
    });
  };

  _renderMainContent = (shouldShowRules, autoFetchLiveSamples) => {
    const { data, isLoading, datasetSemanticInfo } = this.props;

    if (shouldShowRules) {
      return (
        <>
          <DataSetRecipeTab data={data} />
        </>
      );
    }

    if (data.nexset_type === nexsetTypes.api_endpoint) {
      const { endpoint_spec: endpointSpec } = data;

      return (
        <>
          {endpointSpec && (
            <Stack gap="16px" padding="0 16px">
              <Stack gap="8px" direction="row" alignItems="center">
                <Chip
                  label={endpointSpec.method}
                  color={getHttpMethodColor(endpointSpec.method)}
                  variant="outlined"
                />

                <Typography variant="body2">
                  {endpointSpec.route || 'No route configured'}
                </Typography>
              </Stack>
              {hasEndpointConfiguration(endpointSpec) ? (
                <Stack gap="8px">
                  <ApiServerParameterTable params={endpointSpec.headers || []} title="Headers" />

                  <ApiServerParameterTable
                    params={endpointSpec.path_params || []}
                    title="Path Parameters"
                  />

                  <ApiServerParameterTable
                    params={endpointSpec.query_params || []}
                    title="Query Parameters"
                  />

                  <ApiServerParameterTable
                    body={endpointSpec.body}
                    title="Request Body"
                    subtitle="application/json"
                  />
                </Stack>
              ) : (
                <Box className="no-endpoint-spec">
                  <Typography variant="body2">No configured endpoint specification</Typography>
                </Box>
              )}
            </Stack>
          )}
        </>
      );
    }

    return (
      <>
        <Stack direction="row" spacing={2} padding="12px" alignItems="center">
          <h2>Schema & Samples</h2>
          <SemanticSchemaGenerateButton
            isLoading={isLoading.generateSemanticSchema}
            isSuccess={!!datasetSemanticInfo}
            onClick={this._handleGenerateSemanticSchemaClicked}
          />

          {datasetSemanticInfo?.dataset_sensitivity && (
            <SemanticSensitivityBadge semanticInfo={datasetSemanticInfo} size="lg" />
          )}
        </Stack>
        <DataSetDataBrowserContainer data={data} autoFetchLiveSamples={autoFetchLiveSamples} />
      </>
    );
  };

  _handleGenerateSemanticSchemaClicked = () => {
    this.props.generateSemanticSchema(this.props.data.id);
  };

  render() {
    const { data, doc, isLoading, updateDatasetDoc, datasetSemanticInfo } = this.props;

    const showLoading = !data || isLoading.dataset || isLoading.doc;

    if (showLoading) {
      return (
        <div className="tab-content preview-tab dataset-overview-tab">
          <div className={styles.loadingIndicator}>
            <CircularProgress size={40} />
            <div style={{ color: 'var(--color-greyscale-600)', fontSize: 'var(--font-size-base)' }}>
              Loading dataset details...
            </div>
          </div>
        </div>
      );
    }

    const isOperationalNexset = isOperational(data);
    const isApiServerFlow = data.flow_type === FLOW_TYPE_API_SERVER;
    const shouldShowDocumentation = !isOperationalNexset;
    const shouldShowRules = isOperationalNexset && !isApiServerFlow;

    let autoFetchLiveSamples = false;

    // This nested structure is intentionally kept for safety and clarity:
    // 1. First check if this is a connector type that needs special handling
    // 2. Then verify it's a detected dataset with required properties
    // 3. Finally check if it has a mock schema that needs auto-fetching
    // We keep the nesting to make the dependencies clear and avoid unnecessary comparisons
    //TODO: this is a hack to get live samples for syslog detected datasets. We will get rid of this once the backend is updated
    const isRelevantConnectorForLiveSamples =
      data?.connector_type === 'nexla_rest' || data?.connector_type === 'syslog';

    if (isRelevantConnectorForLiveSamples) {
      let isDetectedDataset =
        data?.dataSetType === 'source' &&
        !isNil(data?.data_source_id) &&
        !isNil(data?.source_schema);
      if (isDetectedDataset) {
        //check if the source_schema is a mock schema (either an empty object or a mock syslog schema)
        const isMockSchema =
          isEmpty(data?.source_schema) || isEqual(data?.source_schema, MOCK_SYSLOG_DETECTED_SCHEMA);

        if (isMockSchema) {
          autoFetchLiveSamples = true;
        }
      }
    }

    const sidebarContentView = this._renderSidebarContent();
    const mainContentView = this._renderMainContent(shouldShowRules, autoFetchLiveSamples);

    return (
      <div className="tab-content preview-tab dataset-overview-tab">
        <div className={`detail-view-two-col`}>
          <div className="detail-view-main-col">
            {shouldShowDocumentation && (
              <DocEditor
                data={data}
                doc={doc}
                isLoading={isLoading.doc}
                updateDoc={updateDatasetDoc}
                resourceType={RESOURCE_TYPE_DATA_SET}
                useGenAi
              />
            )}
            {mainContentView}
          </div>
          <div className="detail-view-side-col">{sidebarContentView}</div>
        </div>
      </div>
    );
  }
}

const DataSetCardOverviewManager = (props) => {
  const { data } = props;
  if (isSharedDataset(data)) {
    return <SharedDatasetOverviewTab dataset={data} />;
  }

  return <DataSetCardOverviewTab {...props} />;
};

export default DataSetCardOverviewManager;
